/**
 * Students {@link DataTable} for the management page.
 * List + filters + expanded details; delete lives in a modal.
 */
import * as React from 'react';
import { User as UserIcon } from 'lucide-react';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { DataTable } from '@/components/common/data-table';
import { Button } from '@/components/forms/Button';
import { StudentRow } from './StudentRow';
import { StudentCard } from './StudentCard';
import { StudentFiltersContent } from './StudentFilters';
import { DetailsViewContent } from '@/components/common/details/DetailsViewContent';
import { renderStudentDetails } from './renderStudentDetails';
import { useDataTable } from '@/hooks/useDataTable';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getStudents, exportStudentsExcel } from '@/services/inperson/students.service';
import { deleteUser } from '@/services/users.service';
import { DeleteModal } from '@/components/common/modals/DeleteModal';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { cn } from '@/lib/utils';
import { getRoleLabelPlural } from '@/utils/role-label-utils';
import {
  DEFAULT_STUDENT_FILTERS,
  ALL_FILTER_GROUP_IDS,
  FILTER_GROUP_LABELS,
  type FilterGroupId,
} from '@/components/students/student-configs';
import type { ViewMode } from '@/types/data-table.types';
import type {
  Student,
  StudentAccompanimentType,
  StudentFilters,
} from '@/types/inperson/student.types';
import type { ElementWidth } from '@/types/interface-elements.types';
import {
  buildStudentInPersonListQueryParams,
  hasStudentInPersonActiveFilters,
  type StudentDataTablePageActions,
} from './student-table-utils';

/** Props for {@link StudentsDataTable}. */
interface StudentsDataTableProps {
  students?: Student[];
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  onEditStudent?: (id: string) => void;
  enableFilters?: boolean;
  defaultFilters?: StudentFilters;
  layoutWidth?: ElementWidth;
  isSection?: boolean;
  pageActions?: StudentDataTablePageActions;
  showCreateButton?: boolean;
  onCreateStudent?: () => void;
}

/** Students {@link DataTable} for the management page. */
export const StudentsDataTable: React.FC<StudentsDataTableProps> = ({
  students: externalStudents,
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange,
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  showCreateButton = false,
  onCreateStudent,
  onEditStudent,
  enableFilters = true,
  defaultFilters = DEFAULT_STUDENT_FILTERS,
  layoutWidth: layoutWidthProp = 'full',
  isSection = false,
  pageActions,
}) => {
  const queryClient = useQueryClient();
  const showExport = pageActions?.canExportCsv === true;
  const [isExporting, setIsExporting] = React.useState(false);

  const dt = useDataTable<StudentFilters, FilterGroupId>({
    tableName: 'students',
    defaultFilters,
    allFilterGroupIds: ALL_FILTER_GROUP_IDS,
    layoutWidth: layoutWidthProp,
    isSection,
    externalViewMode,
    onExternalViewModeChange: externalOnViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
  });

  const { data: apiResponse, isLoading } = useQuery({
    queryKey: ['users', 'student', dt.currentPage, dt.itemsPerPage, dt.filters],
    queryFn: async () =>
      getStudents(
        buildStudentInPersonListQueryParams(dt.filters, {
          page: dt.currentPage,
          pageSize: dt.itemsPerPage,
        }),
      ),
    enabled: !externalStudents,
  });

  const data = React.useMemo(
    () => externalStudents ?? ((apiResponse?.data || []) as Student[]),
    [externalStudents, apiResponse?.data],
  );
  const totalItems = apiResponse?.total || data.length;
  const isEmptyForExport = React.useMemo(
    () => (externalStudents ? data.length === 0 : !isLoading && totalItems === 0),
    [externalStudents, data.length, isLoading, totalItems],
  );
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = startIndex + dt.itemsPerPage;

  // Fallback query when the expanded record is not on the current page.
  const {
    data: expandedStudentData,
    isLoading: isLoadingExpandedStudent,
    isError: isExpandedStudentError,
    error: expandedStudentError,
  } = useQuery({
    queryKey: ['users', 'student', expandedRowId],
    queryFn: async () => {
      if (!expandedRowId) return null;
      const { getStudent } = await import('@/services/inperson/students.service');
      return await getStudent(expandedRowId);
    },
    enabled: !!expandedRowId,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isExpandedStudentError,
    error: expandedStudentError,
    catalogKey: 'studentNotFound',
    onClose: onCloseDetails,
    entityId: expandedRowId,
  });

  const expandedItem = expandedStudentData ?? null;

  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Étudiant supprimé avec succès !');
      dt.setDeleteId(null);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const studentToDelete = React.useMemo(
    () => data.find((s) => s.id === dt.deleteId),
    [data, dt.deleteId],
  );

  const hasActiveFilters = React.useMemo(
    () => hasStudentInPersonActiveFilters(dt.filters),
    [dt.filters],
  );

  const clearFilters = React.useCallback(() => {
    dt.setFilters(defaultFilters);
    dt.setCurrentPage(1);
  }, [dt, defaultFilters]);

  const toggleStatusFilter = React.useCallback(
    (status: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        statuses: prev.statuses.includes(status)
          ? prev.statuses.filter((s) => s !== status)
          : [...prev.statuses, status],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const toggleGradeLevelFilter = React.useCallback(
    (levelId: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        levelIds: prev.levelIds.includes(levelId)
          ? prev.levelIds.filter((id) => id !== levelId)
          : [...prev.levelIds, levelId],
        classIds: [],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const toggleSubjectFilter = React.useCallback(
    (subjectId: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        subjects: prev.subjects.includes(subjectId)
          ? prev.subjects.filter((id) => id !== subjectId)
          : [...prev.subjects, subjectId],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setEstablishment = React.useCallback(
    (id: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, establishmentId: id, classIds: [] }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setClass = React.useCallback(
    (classId: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, classIds: classId ? [classId] : [] }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setSpecialties = React.useCallback(
    (ids: string[]) => {
      dt.setFilters((prev) => ({ ...prev, specialtyIds: ids }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setCustomDateRange = React.useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      dt.setFilters((prev) => ({ ...prev, createdFrom: from, createdTo: to }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const toggleIsMinor = React.useCallback(
    (value: boolean | undefined) => {
      dt.setFilters((prev) => ({ ...prev, isMinor: value }));
    },
    [dt],
  );

  const toggleHasSpecialNeeds = React.useCallback(
    (value: boolean | undefined) => {
      dt.setFilters((prev) => ({ ...prev, hasSpecialNeeds: value }));
    },
    [dt],
  );

  const toggleAccompanimentType = React.useCallback(
    (value: StudentAccompanimentType) => {
      dt.setFilters((prev) => ({
        ...prev,
        accompanimentTypes: prev.accompanimentTypes.includes(value)
          ? prev.accompanimentTypes.filter((t) => t !== value)
          : [...prev.accompanimentTypes, value],
      }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setExitDateRange = React.useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      dt.setFilters((prev) => ({ ...prev, exitDateFrom: from, exitDateTo: to }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const setExitReason = React.useCallback(
    (reason: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, exitReason: reason }));
    },
    [dt],
  );

  const setSearch = React.useCallback(
    (value: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, search: value }));
      dt.setCurrentPage(1);
    },
    [dt],
  );

  const filtersContent = React.useMemo(
    () => (
      <StudentFiltersContent
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        onSetSearch={setSearch}
        onToggleStatus={toggleStatusFilter}
        onToggleGradeLevel={toggleGradeLevelFilter}
        onToggleSubject={toggleSubjectFilter}
        onSetEstablishment={setEstablishment}
        onSetClass={setClass}
        onSetSpecialties={setSpecialties}
        onSetCustomDateRange={setCustomDateRange}
        onToggleIsMinor={toggleIsMinor}
        onToggleHasSpecialNeeds={toggleHasSpecialNeeds}
        onToggleAccompanimentType={toggleAccompanimentType}
        onSetExitDateRange={setExitDateRange}
        onSetExitReason={setExitReason}
      />
    ),
    [
      dt.filters,
      dt.allowedFilterIds,
      dt.visibleFilterIds,
      setSearch,
      toggleStatusFilter,
      toggleGradeLevelFilter,
      toggleSubjectFilter,
      setEstablishment,
      setClass,
      setSpecialties,
      setCustomDateRange,
      toggleIsMinor,
      toggleHasSpecialNeeds,
      toggleAccompanimentType,
      setExitDateRange,
      setExitReason,
    ],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: () => filtersContent,
      },
    ],
    [filtersContent],
  );

  const handleExportExcel = React.useCallback(async () => {
    if (externalStudents ? data.length === 0 : !isLoading && totalItems === 0) {
      toast.warning(getErrorMessage('exportEmpty'));
      return;
    }
    setIsExporting(true);
    try {
      await exportStudentsExcel(buildStudentInPersonListQueryParams(dt.filters));
      toast.success('Export Excel réussi !');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error(getErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  }, [data.length, dt.filters, externalStudents, isLoading, totalItems]);

  return (
    <DataTable
      data={data}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="élèves"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={11}
      toolbarActions={
        <>
          {enableFilters && (
            <Button
              preset="toolbar-filter"
              className={cn(dt.showFilters && 'section-action-button-active')}
              showBadge={hasActiveFilters}
              onClick={() => {
                const next = !dt.showFilters;
                dt.setShowFilters(next);
                if (!next) dt.setShowConfigure(false);
              }}
            />
          )}
          {showExport && (
            <Button
              preset="toolbar-export"
              tooltipText={
                isEmptyForExport && !isExporting
                  ? getErrorMessage('exportEmpty')
                  : 'Exporter la liste des élèves en Excel'
              }
              onClick={handleExportExcel}
              loading={isExporting}
              disabled={isEmptyForExport}
            />
          )}
          {showCreateButton && onCreateStudent && (
            <Button
              preset="toolbar-create"
              tooltipText="Créer un élève"
              onClick={() => {
                onCloseDetails?.();
                onCreateStudent();
              }}
            />
          )}
        </>
      }
      filtersPanel={
        enableFilters ? (
          <DataTableFilters
            show={dt.showFilters}
            hasActiveFilters={hasActiveFilters}
            filterGroups={filterGroups}
            onClearFilters={clearFilters}
            resultCount={totalItems}
            resultLabel="élève(s)"
            onToggleConfigure={
              dt.allowedFilterIds.length > 0 ? () => dt.setShowConfigure((v) => !v) : undefined
            }
            showConfigure={dt.showConfigure}
            configureContent={
              <div className="flex flex-wrap gap-3">
                {ALL_FILTER_GROUP_IDS.filter((id) => dt.allowedFilterIds.includes(id)).map((id) => {
                  const isOn = dt.filterVisibility[id] ?? true;
                  return (
                    <Button
                      key={id}
                      variant="custom"
                      size="sm"
                      onClick={() => dt.handleToggleFilterVisibility(id)}
                      disabled={dt.isSavingFilterVisibility}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                        dt.isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
                        isOn
                          ? 'bg-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)] text-white'
                          : 'bg-transparent border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] text-muted-foreground hover:border-[var(--color-1-nuance-2)]',
                      )}
                    >
                      {FILTER_GROUP_LABELS[id]}
                    </Button>
                  );
                })}
              </div>
            }
          />
        ) : undefined
      }
      renderTableHead={() => (
        <tr>
          <th>Nom</th>
          <th className="text-center">Genre</th>
          <th className="text-center">Niveau</th>
          <th className="text-center">Statut</th>
          <th className="text-center">Profil</th>
          <th className="text-center">Établissement(s)</th>
          <th className="text-center">Date d'entrée</th>
          <th className="text-center">Date de désactivation</th>
          <th className="text-center">Motif de sortie</th>
          <th className="text-center">Taux de présence</th>
          <th className="w-20 text-center">Actions</th>
        </tr>
      )}
      renderTableRow={(student) => (
        <StudentRow
          student={student as Student}
          onView={onToggleRow ? () => onToggleRow(student.id) : undefined}
          onEdit={onEditStudent}
          onDelete={(id) => dt.setDeleteId(id)}
          pageActions={pageActions}
        />
      )}
      expandedRowId={dt.expandedRowId}
      renderExpandedRow={(student) => (
        <DetailsViewContent>{renderStudentDetails(student)}</DetailsViewContent>
      )}
      expandedRowRef={dt.expandedRowRef}
      expandedCollapseRef={dt.expandedCollapseRef}
      renderCard={(student) => (
        <StudentCard
          student={student as Student}
          onView={onToggleRow ? () => onToggleRow(student.id) : undefined}
          onEdit={onEditStudent}
          onDelete={(id) => dt.setDeleteId(id)}
          pageActions={pageActions}
        />
      )}
      emptyIcon={UserIcon}
      emptyText="Aucun élève trouvé"
      title={getRoleLabelPlural('student')}
      sidebarMenuId="menu-students"
      isSection={isSection}
      expandedItem={expandedItem as Student | null}
      isExpandedDetailLoading={isLoadingExpandedStudent}
      onCloseDetails={onCloseDetails}
      renderDetailsContent={(student) => (
        <DetailsViewContent>{renderStudentDetails(student)}</DetailsViewContent>
      )}
      modals={
        <DeleteModal
          open={!!dt.deleteId}
          onClose={() => dt.setDeleteId(null)}
          onConfirm={() => {
            if (dt.deleteId) deleteMutation.mutate(dt.deleteId);
          }}
          itemName={studentToDelete ? studentToDelete.displayName || 'Cet élève' : ''}
          isLoading={deleteMutation.isPending}
        />
      }
    />
  );
};
